import { SensorAlert } from 'common';
import { LucideXCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { YStack, XStack, Text } from 'tamagui';
import { gradients } from 'tamagui-config';

import { LoadingOverlay } from '../../../components/common/LoadingOverlay';
import { TripleGlowBackground } from '../../../components/common/TripleGlowBackground';
import { useElectricalAlertContext } from '../../../contexts/ElectricalAlertContext';
import { useGetRawElectricalData } from '../../../hooks';

const POWER_STATUS = {
  HEALTHY: 'healthy',
  SURGE: 'surge',
  LOW: 'low',
} as const;

const getPowerStatusLabels = (t: any) => ({
  HEALTHY: t('electrical.status.healthy'),
  SURGE: t('electrical.status.powerSurge'),
  LOW: t('electrical.status.lowPower'),
});

const SYSTEM_STATUS = {
  GOOD: 'good',
  POOR: 'poor',
  ERROR: 'error',
} as const;

export interface ElectricalStatusContentProps {
  statusIcon: React.ReactNode;
  statusLabel: string;
  gradientColors: string[];
  offlineRoomsCount?: number;
  loading?: boolean;
  currentAmps?: number;
  currentVoltage?: number;
  roomLocation?: string;
  roomAlerts?: any[];
  alerts?: SensorAlert[];
}

function getPowerStatusFromAlerts(alerts: SensorAlert[], t: any): { status: string; label: string } {
  const POWER_STATUS_LABELS = getPowerStatusLabels(t);
  
  if (!alerts || alerts.length === 0) {
    return { status: POWER_STATUS.HEALTHY, label: POWER_STATUS_LABELS.HEALTHY };
  }

  const hasHighVoltage = alerts.some((alert) => alert.alertType === 'HIGH_VOLTAGE');
  const hasLowVoltage = alerts.some((alert) => alert.alertType === 'LOW_VOLTAGE');
  const hasHighCurrent = alerts.some(
    (alert) =>
      alert.alertType === 'HIGH_CURRENT' ||
      alert.alertType === 'HIGH_MCT_CURRENT' ||
      alert.alertType === 'HIGH_BCT_CURRENT',
  );
  const hasLowCurrent = alerts.some(
    (alert) =>
      alert.alertType === 'LOW_CURRENT' ||
      alert.alertType === 'LOW_MCT_CURRENT' ||
      alert.alertType === 'LOW_BCT_CURRENT',
  );

  if (hasHighVoltage && hasHighCurrent) {
    return { status: POWER_STATUS.SURGE, label: POWER_STATUS_LABELS.SURGE };
  } else if (hasLowVoltage && hasLowCurrent) {
    return { status: POWER_STATUS.LOW, label: POWER_STATUS_LABELS.LOW };
  } else {
    return { status: POWER_STATUS.HEALTHY, label: POWER_STATUS_LABELS.HEALTHY };
  }
}

export const ElectricalStatusContent: React.FC<ElectricalStatusContentProps> = ({
  statusIcon,
  statusLabel,
  gradientColors,
  offlineRoomsCount,
  loading = false,
  currentAmps,
  currentVoltage,
  roomLocation,
  roomAlerts,
  alerts,
}) => {
  const { t } = useTranslation();
  const showOfflineRoomsLabel =
    typeof offlineRoomsCount === 'number' && offlineRoomsCount > 0 && !loading;
  const bottomLabel = showOfflineRoomsLabel
    ? t('electrical.roomsOffline', { count: offlineRoomsCount })
    : null;

  const { powerStatus } = useElectricalAlertContext();
  const { data: allDevices } = useGetRawElectricalData();

  const alertsToUse = roomAlerts || alerts || allDevices || [];

  const powerStatusFromAlerts = getPowerStatusFromAlerts(alertsToUse, t);

  const finalPowerStatus = roomLocation ? powerStatusFromAlerts.status : powerStatus; // System health should never use power alert status
  const finalPowerLabel = roomLocation
    ? powerStatusFromAlerts.label
    : powerStatus
      ? powerStatus === t('electrical.status.powerSurge')
        ? t('electrical.status.powerSurge')
        : t('electrical.status.lowPower')
      : null;

  const powerWatts = currentAmps && currentVoltage ? currentAmps * currentVoltage : null;

  const showPowerDisplay =
    roomLocation && (finalPowerStatus !== POWER_STATUS.HEALTHY || currentAmps || powerWatts);

  const POWER_STATUS_LABELS = getPowerStatusLabels(t);
  
  const displayStatusLabel =
    (roomLocation && finalPowerStatus !== POWER_STATUS.HEALTHY ? finalPowerLabel : statusLabel) ||
    POWER_STATUS_LABELS.HEALTHY;

  const finalGradientColors =
    roomLocation &&
    (finalPowerStatus === POWER_STATUS.SURGE || finalPowerStatus === POWER_STATUS.LOW)
      ? gradients.error
      : gradientColors;

  const finalStatusIcon = (() => {
    if (
      roomLocation &&
      (finalPowerStatus === POWER_STATUS.SURGE || finalPowerStatus === POWER_STATUS.LOW)
    ) {
      return <LucideXCircle size={64} />;
    }
    if (!roomLocation && (statusLabel.toLowerCase().includes(SYSTEM_STATUS.POOR) || statusLabel.toLowerCase().includes(SYSTEM_STATUS.ERROR))) {
      return <LucideXCircle size={64} />;
    }
    return statusIcon;
  })();

  if (loading) {
    return (
      <YStack flex={1} position="relative">
        <LoadingOverlay isVisible={true} />
      </YStack>
    );
  }
  return (
    <YStack flex={1}>
      <TripleGlowBackground gradientColors={finalGradientColors} />
      <YStack flex={1}>
        <XStack flex={1} gap="$6" alignItems="center" justifyContent="center">
          {finalStatusIcon}
          <YStack alignItems="center">
            <Text fontFamily="$heading" fontWeight="$9.5" fontSize="$9.5" color="$white12">
              {displayStatusLabel.charAt(0).toUpperCase() + displayStatusLabel.slice(1)}
            </Text>
          </YStack>
        </XStack>
      </YStack>
      {roomLocation
        ? showPowerDisplay &&
          currentAmps && (
            <Text fontSize="$6" color="$white10" textAlign="center">
              {currentAmps.toFixed(1)} A
            </Text>
          )
        : bottomLabel && (
            <Text fontSize="$9.5" textAlign="center" color="$white12">
              {bottomLabel}
            </Text>
          )}
    </YStack>
  );
};
